import React, { useEffect, useState, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import {
  Warehouse, Plus, Edit2, Trash2, Power, X, Check,
  Loader, CheckCircle, AlertCircle, MapPin, Phone, User,
  Upload, Download, FileSpreadsheet, History,
  ArrowUpCircle, ArrowDownCircle, Search, Boxes, LayoutGrid,
  PackageOpen, ArrowRight, PackageSearch, Package
} from 'lucide-react';
import {
  getEntrepots, createEntrepot, updateEntrepot,
  deleteEntrepot, toggleActiveEntrepot, importStockEntrepot,
  importStockMultiEntrepots,
  getMouvementsStock,
  addEntrepotRayon, updateEntrepotRayon, deleteEntrepotRayon,
  addEntrepotEmplacement, updateEntrepotEmplacement, deleteEntrepotEmplacement,
  getEntrepotEmplacementStock, placerStock,
  getProduits, getAllStockDistributions,
} from '../services/api';
import '../styles/Entrepots.css';

const EMPTY_FORM = { nom: '', adresse: '', responsable: '', telephone: '' };

export default function Entrepots() {
  const [entrepots,  setEntrepots]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [toast,      setToast]      = useState(null);

  const [showModal,  setShowModal]  = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [formData,   setFormData]   = useState(EMPTY_FORM);

  // Import stock
  const [showImportModal, setShowImportModal] = useState(false);
  const [importEntrepot,  setImportEntrepot]  = useState(null);
  const [importRows,      setImportRows]      = useState([]);
  const [importMode,      setImportMode]      = useState('remplacement'); // 'remplacement' | 'ajout'
  const [importError,     setImportError]     = useState('');
  const [importing,       setImporting]       = useState(false);
  const [importResult,    setImportResult]    = useState(null);
  const importFileRef = useRef(null);

  // Import multi-entrepôts (un seul fichier, plusieurs entrepôts ciblés)
  const [showMultiImport, setShowMultiImport]   = useState(false);
  const [multiRows,       setMultiRows]         = useState([]);
  const [multiMode,       setMultiMode]         = useState('remplacement');
  const [multiError,      setMultiError]        = useState('');
  const [multiImporting,  setMultiImporting]    = useState(false);
  const [multiResult,     setMultiResult]       = useState(null);
  const multiFileRef = useRef(null);

  // Plan de l'entrepôt (rayons / emplacements)
  const [showPlanModal,  setShowPlanModal]  = useState(false);
  const [planEntrepot,   setPlanEntrepot]   = useState(null);
  const [newRayonNom,    setNewRayonNom]    = useState('');
  const [newEmpByRayon,  setNewEmpByRayon]  = useState({}); // { [rayonId]: nom }
  const [planBusy,       setPlanBusy]       = useState(false);

  // Placement / rangement du stock dans les emplacements
  const [showPlaceModal, setShowPlaceModal] = useState(false);
  const [placeEntrepot,  setPlaceEntrepot]  = useState(null);
  const [placeData,      setPlaceData]      = useState(null); // { entrepot, rayons, nonRange }
  const [placeLoading,   setPlaceLoading]   = useState(false);
  const [moveForm,       setMoveForm]       = useState(null); // { produit, produitNom, source, sourceNom, max }
  const [moveDest,       setMoveDest]       = useState('');
  const [moveQty,        setMoveQty]        = useState('');
  const [moving,         setMoving]         = useState(false);

  // Recherche produit → rangement dans un entrepôt / emplacement
  const [showFindModal, setShowFindModal] = useState(false);
  const [findLoading,   setFindLoading]   = useState(false);
  const [findProduits,  setFindProduits]  = useState([]);
  const [findDist,      setFindDist]      = useState({});   // { [produitId]: [{ entrepotId, nom, quantiteStock, emplacements[] }] }
  const [findSearch,    setFindSearch]    = useState('');
  const [findProduit,   setFindProduit]   = useState(null); // produit sélectionné
  const [findEntrepot,  setFindEntrepot]  = useState('');   // entrepôt de destination
  const [findSource,    setFindSource]    = useState('');   // emplacement source ('__none__' = non rangé)
  const [findDest,      setFindDest]      = useState('');   // emplacement destination ('__none__' = non rangé)
  const [findQty,       setFindQty]       = useState('');
  const [findBusy,      setFindBusy]      = useState(false);

  // Journal
  const [showJournalModal, setShowJournalModal] = useState(false);
  const [journalEntrepot,  setJournalEntrepot]  = useState(null);
  const [journalLoading,   setJournalLoading]   = useState(false);
  const [journalRows,      setJournalRows]      = useState([]);
  const [journalSearch,    setJournalSearch]    = useState('');
  const [journalType,      setJournalType]      = useState('');

  const nbActifs   = useMemo(() => entrepots.filter(e => e.actif).length,  [entrepots]);
  const nbInactifs = useMemo(() => entrepots.filter(e => !e.actif).length, [entrepots]);

  useEffect(() => { loadData(); }, []);

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3500);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const r = await getEntrepots();
      setEntrepots(r.data.data || []);
    } catch {
      showToast('error', 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => { setEditing(null); setFormData(EMPTY_FORM); setShowModal(true); };
  const openEdit   = (e) => {
    setEditing(e);
    setFormData({ nom: e.nom || '', adresse: e.adresse || '', responsable: e.responsable || '', telephone: e.telephone || '' });
    setShowModal(true);
  };
  const closeModal = () => { setShowModal(false); setEditing(null); setFormData(EMPTY_FORM); };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setSubmitting(true);
    try {
      if (editing) {
        await updateEntrepot(editing._id, formData);
        showToast('success', 'Entrepôt modifié avec succès');
      } else {
        await createEntrepot(formData);
        showToast('success', 'Entrepôt créé avec succès');
      }
      closeModal();
      loadData();
    } catch (err) {
      showToast('error', err.response?.data?.message || "Erreur lors de l'enregistrement");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet entrepôt ?')) return;
    try {
      await deleteEntrepot(id);
      showToast('success', 'Entrepôt supprimé');
      loadData();
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  // ── Import stock ──
  const openImport = (entrepot) => {
    setImportEntrepot(entrepot);
    setImportRows([]);
    setImportError('');
    setImportResult(null);
    setImportMode('remplacement');
    if (importFileRef.current) importFileRef.current.value = '';
    setShowImportModal(true);
  };
  const closeImport = () => {
    if (importing) return;
    setShowImportModal(false);
    setImportEntrepot(null);
    setImportRows([]);
    setImportError('');
    setImportResult(null);
  };

  const downloadStockTemplate = () => {
    const headers = ['Code Produit', 'Code Barres', 'Nom (info)', 'Quantité Stock'];
    const examples = [
      ['PROD-001', '', 'Produit exemple A', 100],
      ['', '3760123456789', 'Produit exemple B', 50],
    ];
    const ws = XLSX.utils.aoa_to_sheet([headers, ...examples]);
    ws['!cols'] = [{ wch: 18 }, { wch: 18 }, { wch: 28 }, { wch: 14 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Stock');
    XLSX.writeFile(wb, 'modele_import_stock_entrepot.xlsx');
  };

  const handleImportFile = (ev) => {
    const file = ev.target.files?.[0];
    setImportError('');
    setImportResult(null);
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        if (rows.length < 2) { setImportError('Le fichier ne contient pas de données'); return; }
        const parsed = [];
        for (let i = 1; i < rows.length; i++) {
          const [code, barre, nom, qte] = rows[i].map(v => String(v ?? '').trim());
          if (!code && !barre && !qte) continue;
          parsed.push({
            codeProduit: code,
            codeBarres: barre,
            nom,
            quantiteStock: Number(String(qte).replace(',', '.')) || 0,
          });
        }
        if (!parsed.length) { setImportError('Aucune ligne valide trouvée'); return; }
        setImportRows(parsed);
      } catch (err) {
        setImportError('Fichier invalide : ' + err.message);
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleImportSubmit = async () => {
    if (!importEntrepot || !importRows.length) return;
    setImporting(true);
    setImportError('');
    try {
      const { data } = await importStockEntrepot({
        entrepot: importEntrepot._id,
        mode: importMode,
        items: importRows.map(r => ({
          codeProduit: r.codeProduit,
          codeBarres: r.codeBarres,
          quantiteStock: r.quantiteStock,
        })),
      });
      setImportResult(data.data);
      showToast('success', data.message || 'Import terminé');
    } catch (err) {
      setImportError(err.response?.data?.message || "Erreur lors de l'import");
    } finally {
      setImporting(false);
    }
  };

  // ── Import multi-entrepôts ──
  const openMultiImport = () => {
    setMultiRows([]);
    setMultiError('');
    setMultiResult(null);
    setMultiMode('remplacement');
    if (multiFileRef.current) multiFileRef.current.value = '';
    setShowMultiImport(true);
  };
  const closeMultiImport = () => {
    if (multiImporting) return;
    setShowMultiImport(false);
    setMultiRows([]); setMultiError(''); setMultiResult(null);
  };
  const downloadMultiTemplate = () => {
    const headers = ['Entrepôt', 'Code Produit', 'Code Barres', 'Nom (info)', 'Quantité Stock'];
    const examples = [
      [entrepots[0]?.nom || 'Entrepôt central', 'PROD-001', '', 'Produit A', 100],
      [entrepots[1]?.nom || entrepots[0]?.nom || 'Entrepôt central', '', '3760123456789', 'Produit B', 50],
    ];
    const ws = XLSX.utils.aoa_to_sheet([headers, ...examples]);
    ws['!cols'] = [{ wch: 22 }, { wch: 18 }, { wch: 18 }, { wch: 26 }, { wch: 14 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Stock');
    XLSX.writeFile(wb, 'modele_import_stock_multi_entrepots.xlsx');
  };
  const handleMultiFile = (ev) => {
    const file = ev.target.files?.[0];
    setMultiError(''); setMultiResult(null);
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        if (rows.length < 2) { setMultiError('Le fichier ne contient pas de données'); return; }
        const parsed = [];
        for (let i = 1; i < rows.length; i++) {
          const [entrepot, code, barre, nom, qte] = rows[i].map(v => String(v ?? '').trim());
          if (!entrepot && !code && !barre && !qte) continue;
          parsed.push({
            entrepot,
            codeProduit: code,
            codeBarres: barre,
            nom,
            quantiteStock: Number(String(qte).replace(',', '.')) || 0,
          });
        }
        if (!parsed.length) { setMultiError('Aucune ligne valide trouvée'); return; }
        setMultiRows(parsed);
      } catch (err) {
        setMultiError('Fichier invalide : ' + err.message);
      }
    };
    reader.readAsBinaryString(file);
  };
  const handleMultiSubmit = async () => {
    if (!multiRows.length) return;
    setMultiImporting(true);
    setMultiError('');
    try {
      const { data } = await importStockMultiEntrepots({
        mode: multiMode,
        items: multiRows.map(r => ({
          entrepot: r.entrepot,
          codeProduit: r.codeProduit,
          codeBarres: r.codeBarres,
          quantiteStock: r.quantiteStock,
        })),
      });
      setMultiResult(data.data);
      showToast('success', data.message || 'Import terminé');
    } catch (err) {
      setMultiError(err.response?.data?.message || 'Erreur lors de l\'import');
    } finally {
      setMultiImporting(false);
    }
  };

  // Lignes regroupées par entrepôt pour la preview
  const multiGrouped = useMemo(() => {
    const m = new Map();
    multiRows.forEach(r => {
      const k = r.entrepot || '(non défini)';
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(r);
    });
    return [...m.entries()];
  }, [multiRows]);

  // ── Journal entrepot ──
  const openJournal = async (entrepot) => {
    setJournalEntrepot(entrepot);
    setJournalRows([]);
    setJournalSearch('');
    setJournalType('');
    setShowJournalModal(true);
    setJournalLoading(true);
    try {
      const { data } = await getMouvementsStock({ entrepot: entrepot._id, limit: 500 });
      setJournalRows(data.data || []);
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Erreur lors du chargement du journal');
    } finally {
      setJournalLoading(false);
    }
  };
  const closeJournal = () => {
    setShowJournalModal(false);
    setJournalEntrepot(null);
    setJournalRows([]);
  };
  const journalFiltered = useMemo(() => {
    let r = journalRows;
    if (journalType) r = r.filter(m => m.type === journalType);
    if (journalSearch.trim()) {
      const q = journalSearch.toLowerCase();
      r = r.filter(m =>
        (m.produit?.nom || '').toLowerCase().includes(q) ||
        (m.produit?.codeProduit || '').toLowerCase().includes(q) ||
        (m.reference || '').toLowerCase().includes(q) ||
        (m.motif || '').toLowerCase().includes(q));
    }
    return r;
  }, [journalRows, journalType, journalSearch]);

  // ── Plan de l'entrepôt (rayons / emplacements) ──
  const openPlan = (e) => {
    setPlanEntrepot(e);
    setNewRayonNom('');
    setNewEmpByRayon({});
    setShowPlanModal(true);
  };
  const closePlan = () => {
    setShowPlanModal(false);
    setPlanEntrepot(null);
    loadData();
  };
  // Remplace l'entrepôt courant par sa version à jour (renvoyée par l'API)
  const refreshPlan = (updated) => {
    setPlanEntrepot(updated);
    setEntrepots(prev => prev.map(e => (e._id === updated._id ? updated : e)));
  };
  const planError = (err) => showToast('error', err.response?.data?.message || 'Erreur');

  const handleAddRayon = async () => {
    if (!newRayonNom.trim() || planBusy) return;
    setPlanBusy(true);
    try {
      const { data } = await addEntrepotRayon(planEntrepot._id, { nom: newRayonNom.trim() });
      refreshPlan(data.data);
      setNewRayonNom('');
    } catch (err) { planError(err); } finally { setPlanBusy(false); }
  };
  const handleRenameRayon = async (rayon) => {
    const nom = window.prompt('Nouveau nom du rayon', rayon.nom);
    if (nom == null || !nom.trim() || nom.trim() === rayon.nom) return;
    try {
      const { data } = await updateEntrepotRayon(planEntrepot._id, rayon._id, { nom: nom.trim() });
      refreshPlan(data.data);
    } catch (err) { planError(err); }
  };
  const handleDeleteRayon = async (rayonId) => {
    if (!window.confirm('Supprimer ce rayon et tous ses emplacements ?')) return;
    try {
      const { data } = await deleteEntrepotRayon(planEntrepot._id, rayonId);
      refreshPlan(data.data);
    } catch (err) { planError(err); }
  };
  const handleAddEmp = async (rayonId) => {
    const nom = (newEmpByRayon[rayonId] || '').trim();
    if (!nom) return;
    try {
      const { data } = await addEntrepotEmplacement(planEntrepot._id, rayonId, { nom });
      refreshPlan(data.data);
      setNewEmpByRayon(p => ({ ...p, [rayonId]: '' }));
    } catch (err) { planError(err); }
  };
  const handleRenameEmp = async (rayonId, emp) => {
    const nom = window.prompt("Nouveau nom de l'emplacement", emp.nom);
    if (nom == null || !nom.trim() || nom.trim() === emp.nom) return;
    try {
      const { data } = await updateEntrepotEmplacement(planEntrepot._id, rayonId, emp._id, { nom: nom.trim() });
      refreshPlan(data.data);
    } catch (err) { planError(err); }
  };
  const handleToggleEmp = async (rayonId, emp) => {
    try {
      const { data } = await updateEntrepotEmplacement(planEntrepot._id, rayonId, emp._id, { actif: !emp.actif });
      refreshPlan(data.data);
    } catch (err) { planError(err); }
  };
  const handleDeleteEmp = async (rayonId, empId) => {
    try {
      const { data } = await deleteEntrepotEmplacement(planEntrepot._id, rayonId, empId);
      refreshPlan(data.data);
    } catch (err) { planError(err); }
  };

  // ── Placement / rangement du stock ──
  const loadPlace = async (id) => {
    setPlaceLoading(true);
    try {
      const { data } = await getEntrepotEmplacementStock(id);
      setPlaceData(data.data);
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Erreur de chargement');
    } finally {
      setPlaceLoading(false);
    }
  };
  const openPlace = async (e) => {
    setPlaceEntrepot(e);
    setPlaceData(null);
    setMoveForm(null);
    setShowPlaceModal(true);
    await loadPlace(e._id);
  };
  const closePlace = () => {
    setShowPlaceModal(false);
    setPlaceEntrepot(null);
    setPlaceData(null);
    setMoveForm(null);
  };
  const startMove = (produit, source, sourceNom, max) => {
    setMoveForm({ produit: produit._id, produitNom: produit.nom, source, sourceNom, max });
    setMoveDest('');
    setMoveQty(String(max));
  };
  const submitMove = async () => {
    if (!moveForm) return;
    const qty = Number(moveQty);
    if (!Number.isFinite(qty) || qty <= 0 || qty > moveForm.max) {
      showToast('error', `Quantité invalide (max ${moveForm.max})`); return;
    }
    if (!moveDest) { showToast('error', 'Choisissez une destination'); return; }
    setMoving(true);
    try {
      await placerStock({
        entrepot: placeEntrepot._id,
        produit: moveForm.produit,
        source: moveForm.source,                              // null = depuis « non rangé »
        destination: moveDest === '__none__' ? null : moveDest, // null = vers « non rangé »
        quantite: qty,
      });
      showToast('success', 'Stock rangé');
      setMoveForm(null);
      await loadPlace(placeEntrepot._id);
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Erreur lors du déplacement');
    } finally {
      setMoving(false);
    }
  };
  // Liste plate des emplacements (pour le sélecteur de destination)
  const placeEmplacements = useMemo(() => {
    if (!placeData) return [];
    const list = [];
    for (const r of placeData.rayons || []) {
      for (const emp of r.emplacements || []) {
        list.push({ id: String(emp._id), label: `${r.nom} › ${emp.nom}`, actif: emp.actif });
      }
    }
    return list;
  }, [placeData]);

  // ── Recherche produit → rangement ──
  const loadFindData = async () => {
    setFindLoading(true);
    try {
      const [pRes, dRes] = await Promise.all([getProduits(), getAllStockDistributions()]);
      setFindProduits(pRes.data.data || []);
      setFindDist(dRes.data.data || {});
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Erreur de chargement');
    } finally {
      setFindLoading(false);
    }
  };
  const openFind = async () => {
    setShowFindModal(true);
    setFindSearch(''); setFindProduit(null);
    resetFindForm();
    await loadFindData();
  };
  const closeFind = () => {
    setShowFindModal(false);
    setFindProduit(null); setFindSearch('');
    resetFindForm();
  };
  const resetFindForm = () => {
    setFindEntrepot(''); setFindSource(''); setFindDest(''); setFindQty('');
  };

  // Distribution du produit sélectionné, par entrepôt
  const findProduitDist = useMemo(
    () => (findProduit ? (findDist[String(findProduit._id)] || []) : []),
    [findProduit, findDist]
  );

  // Emplacements de l'entrepôt de destination choisi (depuis le plan de l'entrepôt)
  const findDestEmplacements = useMemo(() => {
    const ent = entrepots.find(e => String(e._id) === String(findEntrepot));
    if (!ent) return [];
    const list = [];
    for (const r of ent.rayons || []) {
      for (const emp of r.emplacements || []) {
        if (emp.actif === false) continue;
        list.push({ id: String(emp._id), label: `${r.nom} › ${emp.nom}` });
      }
    }
    return list;
  }, [entrepots, findEntrepot]);

  // Sources disponibles (emplacements de cet entrepôt où le produit a du stock)
  const findSources = useMemo(() => {
    const ent = findProduitDist.find(d => String(d.entrepotId) === String(findEntrepot));
    if (!ent) return [];
    return (ent.emplacements || []).map(emp => ({
      id: emp.emplacementId ? String(emp.emplacementId) : '__none__',
      label: emp.emplacementId ? `${emp.rayon ? `${emp.rayon} › ` : ''}${emp.nom}` : 'Non rangé',
      qty: emp.quantiteStock,
    }));
  }, [findProduitDist, findEntrepot]);

  const findSourceMax = useMemo(() => {
    const s = findSources.find(x => x.id === findSource);
    return s ? s.qty : 0;
  }, [findSources, findSource]);

  // Sélection d'un produit : pré-remplir avec l'entrepôt qui en détient le plus
  const selectFindProduit = (p) => {
    setFindProduit(p);
    resetFindForm();
    const dist = findDist[String(p._id)] || [];
    if (dist.length) setFindEntrepot(String(dist[0].entrepotId));
  };

  // Changement d'entrepôt : présélectionner la source la mieux fournie
  const changeFindEntrepot = (eid) => {
    setFindEntrepot(eid);
    setFindSource(''); setFindDest(''); setFindQty('');
  };

  const submitFind = async () => {
    if (!findProduit)  { showToast('error', 'Sélectionnez un produit'); return; }
    if (!findEntrepot) { showToast('error', 'Choisissez un entrepôt'); return; }
    if (!findSource)   { showToast('error', 'Choisissez la provenance du stock'); return; }
    if (!findDest)     { showToast('error', 'Choisissez un emplacement de destination'); return; }
    if (findSource === findDest) { showToast('error', 'La provenance et la destination sont identiques'); return; }
    const qty = Number(findQty);
    if (!Number.isFinite(qty) || qty <= 0 || qty > findSourceMax) {
      showToast('error', `Quantité invalide (max ${findSourceMax})`); return;
    }
    setFindBusy(true);
    try {
      await placerStock({
        entrepot: findEntrepot,
        produit: findProduit._id,
        source:      findSource === '__none__' ? null : findSource,
        destination: findDest   === '__none__' ? null : findDest,
        quantite: qty,
      });
      const destLabel = findDest === '__none__'
        ? 'Non rangé'
        : (findDestEmplacements.find(e => e.id === findDest)?.label || 'destination');
      showToast('success', `${qty} × ${findProduit.nom} rangé(s) dans ${destLabel}`);
      // Recharger la distribution pour refléter le nouveau rangement
      const dRes = await getAllStockDistributions();
      setFindDist(dRes.data.data || {});
      setFindSource(''); setFindDest(''); setFindQty('');
      // Garder le modal Rangement de l'entrepôt à jour s'il est ouvert
      if (showPlaceModal && placeEntrepot && String(placeEntrepot._id) === String(findEntrepot)) {
        loadPlace(placeEntrepot._id);
      }
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Erreur lors du rangement');
    } finally {
      setFindBusy(false);
    }
  };

  // Résultats de recherche (nom, code produit, code-barres)
  const findResults = useMemo(() => {
    const q = findSearch.trim().toLowerCase();
    if (!q) return [];
    return findProduits.filter(p =>
      (p.nom || '').toLowerCase().includes(q) ||
      (p.codeProduit || '').toLowerCase().includes(q) ||
      (p.codeBarres || '').toLowerCase().includes(q)
    ).slice(0, 30);
  }, [findSearch, findProduits]);

  const handleToggle = async (id) => {
    try {
      await toggleActiveEntrepot(id);
      loadData();
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Erreur');
    }
  };

  if (loading) return (
    <div className="en-loading"><Loader className="en-spinner" size={26}/><span>Chargement…</span></div>
  );

  return (
    <div className="en-page">

      {/* Toast */}
      {toast && (
        <div className={`en-toast en-toast--${toast.type}`}>
          {toast.type === 'success' ? <CheckCircle size={13}/> : <AlertCircle size={13}/>}
          {toast.text}
        </div>
      )}

      {/* ── Header ── */}
      <div className="en-header">
        <div className="en-header-left">
          <div className="en-header-icon"><Warehouse size={20}/></div>
          <div>
            <h1 className="en-header-title">Entrepôts & Dépôts</h1>
            <p className="en-header-sub">Gérer les entrepôts de stockage</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="en-find-btn" onClick={openFind} disabled={!entrepots.length}>
            <PackageSearch size={14}/> Ranger un produit
          </button>
          <button className="en-multi-btn" onClick={openMultiImport} disabled={!entrepots.length}>
            <Upload size={14}/> Importer stock (multi-entrepôts)
          </button>
          <button className="en-new-btn" onClick={openCreate}><Plus size={15}/> Nouvel entrepôt</button>
        </div>
      </div>

      <div className="en-content">

        {/* KPI */}
        <div className="en-kpi-row">
          {[
            { label: 'Total',     value: entrepots.length, color: '#6366f1', bg: '#eef2ff', icon: Warehouse   },
            { label: 'Actifs',    value: nbActifs,         color: '#16a34a', bg: '#dcfce7', icon: CheckCircle },
            { label: 'Inactifs',  value: nbInactifs,       color: '#dc2626', bg: '#fee2e2', icon: Power       },
          ].map((k, i) => {
            const Icon = k.icon;
            return (
              <div key={i} className="en-kpi" style={{ borderTopColor: k.color }}>
                <div className="en-kpi-icon" style={{ background: k.bg, color: k.color }}><Icon size={14}/></div>
                <div className="en-kpi-val">{k.value}</div>
                <div className="en-kpi-label">{k.label}</div>
              </div>
            );
          })}
        </div>

        {/* Table */}
        {entrepots.length === 0 ? (
          <div className="en-empty">
            <div className="en-empty-icon"><Warehouse size={32}/></div>
            <h3>Aucun entrepôt</h3>
            <p>Créez votre premier entrepôt de stockage</p>
            <button className="en-new-btn" onClick={openCreate}><Plus size={14}/> Créer un entrepôt</button>
          </div>
        ) : (
          <div className="en-card">
            <table className="en-table">
              <thead>
                <tr>
                  <th>Entrepôt</th>
                  <th>Adresse</th>
                  <th>Responsable</th>
                  <th>Téléphone</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {entrepots.map(e => (
                  <tr key={e._id}>
                    <td>
                      <div className="en-name-cell">
                        <div className="en-av">
                          <Warehouse size={14}/>
                        </div>
                        <div>
                          <span className="en-name">{e.nom}</span>
                          {(() => {
                            const nbRayons = e.rayons?.length || 0;
                            const nbEmp = (e.rayons || []).reduce((s, r) => s + (r.emplacements?.length || 0), 0);
                            return nbRayons > 0 ? (
                              <div className="en-plan-badge">
                                <LayoutGrid size={10}/> {nbRayons} rayon{nbRayons > 1 ? 's' : ''} · {nbEmp} emplacement{nbEmp > 1 ? 's' : ''}
                              </div>
                            ) : null;
                          })()}
                        </div>
                      </div>
                    </td>
                    <td>
                      {e.adresse
                        ? <div className="en-info-cell"><MapPin size={11}/> {e.adresse}</div>
                        : <span className="en-muted">—</span>}
                    </td>
                    <td>
                      {e.responsable
                        ? <div className="en-info-cell"><User size={11}/> {e.responsable}</div>
                        : <span className="en-muted">—</span>}
                    </td>
                    <td>
                      {e.telephone
                        ? <div className="en-info-cell"><Phone size={11}/> {e.telephone}</div>
                        : <span className="en-muted">—</span>}
                    </td>
                    <td>
                      <span className="en-badge" style={
                        e.actif
                          ? { background: '#dcfce7', color: '#16a34a' }
                          : { background: '#f1f5f9', color: '#64748b' }
                      }>
                        {e.actif ? 'Actif' : 'Inactif'}
                      </span>
                    </td>
                    <td>
                      <div className="en-row-actions">
                        <button className="en-btn en-btn--plan" title="Plan : rayons & emplacements" onClick={() => openPlan(e)}><Boxes size={13}/></button>
                        <button className="en-btn en-btn--place" title="Rangement : placer le stock dans les emplacements" onClick={() => openPlace(e)}><PackageSearch size={13}/></button>
                        <button className="en-btn en-btn--journal" title="Journal des stocks" onClick={() => openJournal(e)}><History size={13}/></button>
                        <button className="en-btn en-btn--import" title="Importer le stock" onClick={() => openImport(e)} disabled={!e.actif}><Upload size={13}/></button>
                        <button className="en-btn en-btn--edit"   title="Modifier"  onClick={() => openEdit(e)}><Edit2 size={13}/></button>
                        <button
                          className={`en-btn ${e.actif ? 'en-btn--warn' : 'en-btn--green'}`}
                          title={e.actif ? 'Désactiver' : 'Activer'}
                          onClick={() => handleToggle(e._id)}>
                          <Power size={13}/>
                        </button>
                        <button className="en-btn en-btn--danger" title="Supprimer" onClick={() => handleDelete(e._id)}><Trash2 size={13}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Modal Création / Édition ── */}
      {showModal && (
        <>
          <div className="en-overlay" onClick={closeModal}/>
          <div className="en-modal">
            <div className="en-modal-head">
              <div>
                <h2 className="en-modal-title">
                  {editing ? <><Edit2 size={15}/> Modifier l'entrepôt</> : <><Plus size={15}/> Nouvel entrepôt</>}
                </h2>
                {editing && <p className="en-modal-sub">{editing.nom}</p>}
              </div>
              <button className="en-modal-close" onClick={closeModal}><X size={16}/></button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="en-modal-body">

                <div className="en-field">
                  <label>Nom *</label>
                  <input
                    type="text" required autoFocus
                    value={formData.nom}
                    onChange={e => setFormData({ ...formData, nom: e.target.value })}
                    placeholder="Ex : Entrepôt central"/>
                </div>

                <div className="en-field">
                  <label>Adresse <span className="en-opt">(optionnel)</span></label>
                  <input
                    type="text"
                    value={formData.adresse}
                    onChange={e => setFormData({ ...formData, adresse: e.target.value })}
                    placeholder="Ex : Zone industrielle, Dakar"/>
                </div>

                <div className="en-form-row2">
                  <div className="en-field">
                    <label>Responsable <span className="en-opt">(optionnel)</span></label>
                    <input
                      type="text"
                      value={formData.responsable}
                      onChange={e => setFormData({ ...formData, responsable: e.target.value })}
                      placeholder="Ex : Jean Dupont"/>
                  </div>
                  <div className="en-field">
                    <label>Téléphone <span className="en-opt">(optionnel)</span></label>
                    <input
                      type="tel"
                      value={formData.telephone}
                      onChange={e => setFormData({ ...formData, telephone: e.target.value })}
                      placeholder="Ex : +221 77 123 45 67"/>
                  </div>
                </div>

              </div>

              <div className="en-modal-footer">
                <button type="button" className="en-btn-cancel" onClick={closeModal}>Annuler</button>
                <button type="submit" className="en-btn-confirm" disabled={submitting}>
                  {submitting
                    ? <><Loader size={13} className="en-spin"/> Enregistrement…</>
                    : <><Check size={13}/> {editing ? 'Enregistrer' : 'Créer l\'entrepôt'}</>}
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      {/* ── Modal Import Stock ── */}
      {showImportModal && importEntrepot && (
        <>
          <div className="en-overlay" onClick={closeImport}/>
          <div className="en-modal en-modal--lg">
            <div className="en-modal-head">
              <div>
                <h2 className="en-modal-title"><FileSpreadsheet size={15}/> Importer le stock</h2>
                <p className="en-modal-sub">Entrepôt : <strong>{importEntrepot.nom}</strong></p>
              </div>
              <button className="en-modal-close" onClick={closeImport} disabled={importing}><X size={16}/></button>
            </div>

            <div className="en-modal-body">
              {!importResult && (
                <>
                  <div className="en-import-help">
                    <span>Le fichier doit contenir les colonnes : <code>Code Produit</code> (ou <code>Code Barres</code>), <code>Quantité Stock</code>.</span>
                    <button type="button" className="en-template-btn" onClick={downloadStockTemplate}>
                      <Download size={12}/> Télécharger le modèle
                    </button>
                  </div>

                  <div className="en-dropzone" onClick={() => importFileRef.current?.click()}>
                    <Upload size={20}/>
                    <span>Cliquer pour sélectionner un fichier Excel</span>
                    <small>.xlsx, .xls, .csv</small>
                    <input ref={importFileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={handleImportFile}/>
                  </div>

                  {importError && (
                    <div className="en-import-error"><AlertCircle size={12}/> {importError}</div>
                  )}

                  {importRows.length > 0 && (
                    <>
                      <div className="en-import-mode">
                        <label className={`en-mode-opt${importMode === 'remplacement' ? ' en-mode-opt--active' : ''}`}>
                          <input type="radio" name="importMode" value="remplacement"
                            checked={importMode === 'remplacement'}
                            onChange={() => setImportMode('remplacement')}/>
                          <div>
                            <strong>Remplacer</strong>
                            <span>La quantité écrase la valeur existante</span>
                          </div>
                        </label>
                        <label className={`en-mode-opt${importMode === 'ajout' ? ' en-mode-opt--active' : ''}`}>
                          <input type="radio" name="importMode" value="ajout"
                            checked={importMode === 'ajout'}
                            onChange={() => setImportMode('ajout')}/>
                          <div>
                            <strong>Ajouter</strong>
                            <span>La quantité s'ajoute au stock existant</span>
                          </div>
                        </label>
                      </div>

                      <div className="en-import-preview">
                        <div className="en-preview-head">Aperçu — {importRows.length} ligne{importRows.length > 1 ? 's' : ''}</div>
                        <div className="en-preview-table-wrap">
                          <table className="en-preview-table">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Code produit</th>
                                <th>Code-barres</th>
                                <th>Nom</th>
                                <th>Quantité</th>
                              </tr>
                            </thead>
                            <tbody>
                              {importRows.slice(0, 50).map((r, i) => (
                                <tr key={i}>
                                  <td>{i + 1}</td>
                                  <td>{r.codeProduit || '—'}</td>
                                  <td>{r.codeBarres || '—'}</td>
                                  <td>{r.nom || '—'}</td>
                                  <td><strong>{r.quantiteStock}</strong></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {importRows.length > 50 && (
                            <div className="en-preview-more">+ {importRows.length - 50} ligne(s) supplémentaire(s)…</div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}

              {importResult && (
                <div className="en-import-result">
                  <div className="en-import-stats">
                    <div className="en-stat en-stat--green"><strong>{importResult.crees}</strong><span>Créé(s)</span></div>
                    <div className="en-stat en-stat--blue"><strong>{importResult.misAJour}</strong><span>Mis à jour</span></div>
                    <div className="en-stat en-stat--red"><strong>{importResult.erreurs?.length || 0}</strong><span>Erreur(s)</span></div>
                  </div>
                  {importResult.erreurs?.length > 0 && (
                    <div className="en-import-errors">
                      <div className="en-preview-head">Erreurs</div>
                      <ul>
                        {importResult.erreurs.slice(0, 20).map((er, i) => (
                          <li key={i}>Ligne {er.ligne} {er.codeProduit ? `(${er.codeProduit})` : ''} : {er.erreur}</li>
                        ))}
                        {importResult.erreurs.length > 20 && <li>+ {importResult.erreurs.length - 20} autre(s)…</li>}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="en-modal-footer">
              {!importResult ? (
                <>
                  <button type="button" className="en-btn-cancel" onClick={closeImport} disabled={importing}>Annuler</button>
                  <button type="button" className="en-btn-confirm" disabled={importing || !importRows.length} onClick={handleImportSubmit}>
                    {importing
                      ? <><Loader size={13} className="en-spin"/> Import en cours…</>
                      : <><Upload size={13}/> Importer {importRows.length > 0 ? `(${importRows.length})` : ''}</>}
                  </button>
                </>
              ) : (
                <button type="button" className="en-btn-confirm" onClick={closeImport}>
                  <Check size={13}/> Fermer
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── Modal Journal Stock ── */}
      {showJournalModal && journalEntrepot && (
        <>
          <div className="en-overlay" onClick={closeJournal}/>
          <div className="en-modal en-modal--xl">
            <div className="en-modal-head">
              <div>
                <h2 className="en-modal-title"><History size={15}/> Journal des stocks</h2>
                <p className="en-modal-sub">Entrepôt : <strong>{journalEntrepot.nom}</strong></p>
              </div>
              <button className="en-modal-close" onClick={closeJournal}><X size={16}/></button>
            </div>

            <div className="en-modal-body">
              {/* Filtres */}
              <div className="en-journal-filters">
                <div className="en-journal-search">
                  <Search size={13}/>
                  <input type="text" placeholder="Rechercher (produit, code, référence, motif…)"
                    value={journalSearch} onChange={e => setJournalSearch(e.target.value)}/>
                </div>
                <div className="en-journal-types">
                  {[
                    { v: '',        l: 'Tous' },
                    { v: 'entree',  l: 'Entrées' },
                    { v: 'sortie',  l: 'Sorties' },
                  ].map(t => (
                    <button key={t.v} type="button"
                      className={`en-jtype${journalType === t.v ? ' en-jtype--active' : ''}`}
                      onClick={() => setJournalType(t.v)}>{t.l}</button>
                  ))}
                </div>
              </div>

              {journalLoading ? (
                <div className="en-journal-loading"><Loader size={18} className="en-spin"/> Chargement…</div>
              ) : journalFiltered.length === 0 ? (
                <div className="en-journal-empty">
                  <History size={32}/>
                  <span>Aucun mouvement de stock dans cet entrepôt</span>
                </div>
              ) : (
                <div className="en-journal-wrap">
                  <table className="en-journal-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Produit</th>
                        <th>Motif</th>
                        <th>Qté</th>
                        <th>Avant → Après</th>
                        <th>Utilisateur</th>
                        <th>Référence</th>
                      </tr>
                    </thead>
                    <tbody>
                      {journalFiltered.map(m => (
                        <tr key={m._id}>
                          <td className="en-j-date">{new Date(m.createdAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}</td>
                          <td>
                            {m.type === 'entree'
                              ? <span className="en-j-type en-j-type--in"><ArrowUpCircle size={11}/> Entrée</span>
                              : <span className="en-j-type en-j-type--out"><ArrowDownCircle size={11}/> Sortie</span>}
                          </td>
                          <td>
                            <div className="en-j-prod">{m.produit?.nom || '—'}</div>
                            {m.produit?.codeProduit && <div className="en-j-code">{m.produit.codeProduit}</div>}
                          </td>
                          <td><span className="en-j-motif">{m.motif}</span></td>
                          <td><strong className={m.type === 'entree' ? 'en-j-qty-in' : 'en-j-qty-out'}>{m.type === 'entree' ? '+' : '−'}{m.quantite}</strong></td>
                          <td className="en-j-stock">{m.stockAvant} → <strong>{m.stockApres}</strong></td>
                          <td>{m.utilisateur ? `${m.utilisateur.prenom || ''} ${m.utilisateur.nom || ''}`.trim() : '—'}</td>
                          <td><span className="en-j-ref">{m.reference || '—'}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="en-journal-footer-info">{journalFiltered.length} mouvement{journalFiltered.length > 1 ? 's' : ''}</div>
                </div>
              )}
            </div>

            <div className="en-modal-footer">
              <button type="button" className="en-btn-cancel" onClick={closeJournal}>Fermer</button>
            </div>
          </div>
        </>
      )}

      {/* ── Modal Plan : rayons & emplacements ── */}
      {showPlanModal && planEntrepot && (
        <>
          <div className="en-overlay" onClick={closePlan}/>
          <div className="en-modal en-modal--lg">
            <div className="en-modal-head">
              <div>
                <h2 className="en-modal-title"><Boxes size={15}/> Plan de l'entrepôt</h2>
                <p className="en-modal-sub">Entrepôt : <strong>{planEntrepot.nom}</strong> — organisez vos rayons et emplacements pour savoir où est rangé chaque lot.</p>
              </div>
              <button className="en-modal-close" onClick={closePlan}><X size={16}/></button>
            </div>

            <div className="en-modal-body">
              {/* Ajout d'un rayon */}
              <div className="en-plan-addrayon">
                <input
                  type="text"
                  placeholder="Nom du nouveau rayon (ex : Rayon A, Allée 1…)"
                  value={newRayonNom}
                  onChange={e => setNewRayonNom(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddRayon(); } }}
                />
                <button type="button" className="en-btn-confirm" onClick={handleAddRayon} disabled={planBusy || !newRayonNom.trim()}>
                  <Plus size={13}/> Ajouter le rayon
                </button>
              </div>

              {(planEntrepot.rayons?.length || 0) === 0 ? (
                <div className="en-plan-empty">
                  <LayoutGrid size={28}/>
                  <span>Aucun rayon pour le moment. Créez un premier rayon, puis ajoutez-y des emplacements.</span>
                </div>
              ) : (
                <div className="en-plan-rayons">
                  {planEntrepot.rayons.map(rayon => (
                    <div key={rayon._id} className="en-plan-rayon">
                      <div className="en-plan-rayon-head">
                        <div className="en-plan-rayon-title">
                          <LayoutGrid size={14}/>
                          <strong>{rayon.nom}</strong>
                          <span className="en-plan-count">{rayon.emplacements?.length || 0} empl.</span>
                        </div>
                        <div className="en-plan-rayon-actions">
                          <button type="button" className="en-btn en-btn--edit" title="Renommer le rayon" onClick={() => handleRenameRayon(rayon)}><Edit2 size={12}/></button>
                          <button type="button" className="en-btn en-btn--danger" title="Supprimer le rayon" onClick={() => handleDeleteRayon(rayon._id)}><Trash2 size={12}/></button>
                        </div>
                      </div>

                      <div className="en-plan-emps">
                        {(rayon.emplacements || []).map(emp => (
                          <div key={emp._id} className={`en-plan-emp${emp.actif === false ? ' en-plan-emp--off' : ''}`}>
                            <MapPin size={11}/>
                            <span className="en-plan-emp-nom">{emp.nom}</span>
                            <div className="en-plan-emp-actions">
                              <button type="button" title="Renommer" onClick={() => handleRenameEmp(rayon._id, emp)}><Edit2 size={11}/></button>
                              <button type="button" title={emp.actif === false ? 'Réactiver' : 'Désactiver'} onClick={() => handleToggleEmp(rayon._id, emp)}><Power size={11}/></button>
                              <button type="button" title="Supprimer" onClick={() => handleDeleteEmp(rayon._id, emp._id)}><Trash2 size={11}/></button>
                            </div>
                          </div>
                        ))}

                        <div className="en-plan-addemp">
                          <input
                            type="text"
                            placeholder="Nouvel emplacement (ex : A1)"
                            value={newEmpByRayon[rayon._id] || ''}
                            onChange={e => setNewEmpByRayon(p => ({ ...p, [rayon._id]: e.target.value }))}
                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddEmp(rayon._id); } }}
                          />
                          <button type="button" title="Ajouter l'emplacement" onClick={() => handleAddEmp(rayon._id)} disabled={!(newEmpByRayon[rayon._id] || '').trim()}>
                            <Plus size={12}/>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="en-modal-footer">
              <button type="button" className="en-btn-cancel" onClick={closePlan}>Fermer</button>
            </div>
          </div>
        </>
      )}

      {/* ── Modal Rangement / Placement du stock ── */}
      {showPlaceModal && placeEntrepot && (
        <>
          <div className="en-overlay" onClick={closePlace}/>
          <div className="en-modal en-modal--lg">
            <div className="en-modal-head">
              <div>
                <h2 className="en-modal-title"><PackageSearch size={15}/> Rangement du stock</h2>
                <p className="en-modal-sub">Entrepôt : <strong>{placeEntrepot.nom}</strong> — placez le stock « non rangé » dans vos emplacements pour savoir où est chaque lot.</p>
              </div>
              <button className="en-modal-close" onClick={closePlace}><X size={16}/></button>
            </div>

            <div className="en-modal-body">
              {placeLoading ? (
                <div className="en-journal-loading"><Loader size={18} className="en-spin"/> Chargement…</div>
              ) : !placeData ? (
                <div className="en-journal-empty"><PackageOpen size={28}/><span>Aucune donnée</span></div>
              ) : (placeData.rayons || []).length === 0 ? (
                <div className="en-plan-empty">
                  <LayoutGrid size={28}/>
                  <span>Cet entrepôt n'a pas encore de rayons. Créez d'abord son plan (bouton 📦) avant de ranger le stock.</span>
                </div>
              ) : (
                <>
                  {/* Formulaire de déplacement (inline) */}
                  {moveForm && (
                    <div className="en-move-form">
                      <div className="en-move-form-head">
                        Ranger <strong>{moveForm.produitNom}</strong> — depuis <em>{moveForm.sourceNom}</em> (max {moveForm.max})
                      </div>
                      <div className="en-move-form-row">
                        <select value={moveDest} onChange={e => setMoveDest(e.target.value)}>
                          <option value="">— Choisir la destination —</option>
                          {moveForm.source !== null && <option value="__none__">Non rangé</option>}
                          {placeEmplacements
                            .filter(emp => String(emp.id) !== String(moveForm.source) && emp.actif)
                            .map(emp => <option key={emp.id} value={emp.id}>{emp.label}</option>)}
                        </select>
                        <input
                          type="number" min="1" max={moveForm.max}
                          value={moveQty}
                          onChange={e => setMoveQty(e.target.value)}
                          placeholder="Qté"
                        />
                        <button type="button" className="en-btn-confirm" onClick={submitMove} disabled={moving}>
                          {moving ? <Loader size={13} className="en-spin"/> : <><ArrowRight size={13}/> Déplacer</>}
                        </button>
                        <button type="button" className="en-btn-cancel" onClick={() => setMoveForm(null)}>Annuler</button>
                      </div>
                    </div>
                  )}

                  {/* Non rangé */}
                  <div className="en-place-section">
                    <div className="en-place-section-head en-place-section-head--nonrange">
                      <PackageOpen size={14}/> Non rangé
                      <span className="en-plan-count">{placeData.nonRange?.length || 0} produit(s)</span>
                    </div>
                    {(placeData.nonRange || []).length === 0 ? (
                      <div className="en-place-line en-place-line--empty">Tout le stock est rangé 🎉</div>
                    ) : (
                      placeData.nonRange.map((it, i) => (
                        <div key={i} className="en-place-line">
                          <span className="en-place-prod">{it.produit?.nom || '—'}</span>
                          {it.produit?.codeProduit && <span className="en-place-code">{it.produit.codeProduit}</span>}
                          <strong className="en-place-qty">{it.quantiteStock}</strong>
                          <button type="button" className="en-place-move-btn" onClick={() => startMove(it.produit, null, 'Non rangé', it.quantiteStock)}>
                            <ArrowRight size={12}/> Ranger
                          </button>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Rayons & emplacements */}
                  {placeData.rayons.map(rayon => (
                    <div key={rayon._id} className="en-place-rayon">
                      <div className="en-place-rayon-title"><LayoutGrid size={13}/> {rayon.nom}</div>
                      {(rayon.emplacements || []).map(emp => (
                        <div key={emp._id} className="en-place-section">
                          <div className="en-place-section-head">
                            <MapPin size={12}/> {emp.nom}
                            <span className="en-plan-count">{emp.produits?.length || 0}</span>
                          </div>
                          {(emp.produits || []).length === 0 ? (
                            <div className="en-place-line en-place-line--empty">Vide</div>
                          ) : (
                            emp.produits.map((it, i) => (
                              <div key={i} className="en-place-line">
                                <span className="en-place-prod">{it.produit?.nom || '—'}</span>
                                {it.produit?.codeProduit && <span className="en-place-code">{it.produit.codeProduit}</span>}
                                <strong className="en-place-qty">{it.quantiteStock}</strong>
                                <button type="button" className="en-place-move-btn" onClick={() => startMove(it.produit, String(emp._id), `${rayon.nom} › ${emp.nom}`, it.quantiteStock)}>
                                  <ArrowRight size={12}/> Déplacer
                                </button>
                              </div>
                            ))
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                </>
              )}
            </div>

            <div className="en-modal-footer">
              <button type="button" className="en-btn-cancel" onClick={closePlace}>Fermer</button>
            </div>
          </div>
        </>
      )}

      {/* ── Modal Rechercher un produit & le ranger ── */}
      {showFindModal && (
        <>
          <div className="en-overlay" onClick={closeFind}/>
          <div className="en-modal en-modal--lg">
            <div className="en-modal-head">
              <div>
                <h2 className="en-modal-title"><PackageSearch size={15}/> Ranger un produit</h2>
                <p className="en-modal-sub">Cherchez un produit, voyez où se trouve son stock, puis rangez-le dans un emplacement.</p>
              </div>
              <button className="en-modal-close" onClick={closeFind}><X size={16}/></button>
            </div>

            <div className="en-modal-body">
              {findLoading ? (
                <div className="en-journal-loading"><Loader size={18} className="en-spin"/> Chargement du catalogue…</div>
              ) : (
                <>
                  {/* 1 — Recherche */}
                  <div className="en-find-search">
                    <Search size={14}/>
                    <input
                      type="text"
                      autoFocus
                      placeholder="Nom, code produit ou code-barres…"
                      value={findSearch}
                      onChange={e => { setFindSearch(e.target.value); setFindProduit(null); resetFindForm(); }}
                    />
                    {findSearch && (
                      <button type="button" onClick={() => { setFindSearch(''); setFindProduit(null); resetFindForm(); }}>
                        <X size={13}/>
                      </button>
                    )}
                  </div>

                  {/* Résultats */}
                  {!findProduit && findSearch.trim() && (
                    findResults.length === 0 ? (
                      <div className="en-place-line en-place-line--empty">Aucun produit pour « {findSearch} »</div>
                    ) : (
                      <div className="en-find-results">
                        {findResults.map(p => {
                          const total = (findDist[String(p._id)] || []).reduce((s, d) => s + d.quantiteStock, 0);
                          return (
                            <button key={p._id} type="button" className="en-find-result" onClick={() => selectFindProduit(p)}>
                              <Package size={13}/>
                              <span className="en-place-prod">{p.nom}</span>
                              {p.codeProduit && <span className="en-place-code">{p.codeProduit}</span>}
                              <strong className="en-place-qty">{total}</strong>
                            </button>
                          );
                        })}
                      </div>
                    )
                  )}

                  {/* 2 — Produit sélectionné : où est son stock */}
                  {findProduit && (
                    <>
                      <div className="en-find-selected">
                        <div>
                          <Package size={14}/> <strong>{findProduit.nom}</strong>
                          {findProduit.codeProduit && <span className="en-place-code">{findProduit.codeProduit}</span>}
                        </div>
                        <button type="button" className="en-btn-cancel" onClick={() => { setFindProduit(null); resetFindForm(); }}>
                          Changer de produit
                        </button>
                      </div>

                      {findProduitDist.length === 0 ? (
                        <div className="en-plan-empty">
                          <PackageOpen size={28}/>
                          <span>Ce produit n'a de stock dans aucun entrepôt actif. Importez d'abord son stock (bouton « Importer stock ») avant de le ranger.</span>
                        </div>
                      ) : (
                        <>
                          <div className="en-find-dist">
                            {findProduitDist.map(d => (
                              <div key={String(d.entrepotId)} className="en-place-section">
                                <div className="en-place-section-head">
                                  <Warehouse size={12}/> {d.nom}
                                  <span className="en-plan-count">{d.quantiteStock} unité(s)</span>
                                </div>
                                {(d.emplacements || []).map((emp, i) => (
                                  <div key={i} className="en-place-line">
                                    <span className="en-place-prod">
                                      {emp.emplacementId
                                        ? <><MapPin size={11}/> {emp.rayon ? `${emp.rayon} › ` : ''}{emp.nom}</>
                                        : <><PackageOpen size={11}/> Non rangé</>}
                                    </span>
                                    <strong className="en-place-qty">{emp.quantiteStock}</strong>
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>

                          {/* 3 — Formulaire de rangement */}
                          <div className="en-move-form">
                            <div className="en-move-form-head">Ranger dans un emplacement</div>

                            <div className="en-find-form-grid">
                              <label>
                                <span>Entrepôt</span>
                                <select value={findEntrepot} onChange={e => changeFindEntrepot(e.target.value)}>
                                  <option value="">— Choisir —</option>
                                  {findProduitDist.map(d => (
                                    <option key={String(d.entrepotId)} value={String(d.entrepotId)}>
                                      {d.nom} ({d.quantiteStock})
                                    </option>
                                  ))}
                                </select>
                              </label>

                              <label>
                                <span>Provenance</span>
                                <select value={findSource} onChange={e => { setFindSource(e.target.value); setFindQty(''); }} disabled={!findEntrepot}>
                                  <option value="">— Choisir —</option>
                                  {findSources.map(s => (
                                    <option key={s.id} value={s.id}>{s.label} ({s.qty})</option>
                                  ))}
                                </select>
                              </label>

                              <label>
                                <span>Emplacement de destination</span>
                                <select value={findDest} onChange={e => setFindDest(e.target.value)} disabled={!findEntrepot}>
                                  <option value="">— Choisir —</option>
                                  {findSource !== '__none__' && <option value="__none__">Non rangé</option>}
                                  {findDestEmplacements
                                    .filter(emp => emp.id !== findSource)
                                    .map(emp => <option key={emp.id} value={emp.id}>{emp.label}</option>)}
                                </select>
                              </label>

                              <label>
                                <span>Quantité{findSource ? ` (max ${findSourceMax})` : ''}</span>
                                <input
                                  type="number" min="1" max={findSourceMax || undefined}
                                  value={findQty}
                                  onChange={e => setFindQty(e.target.value)}
                                  placeholder="0"
                                  disabled={!findSource}
                                />
                              </label>
                            </div>

                            {findEntrepot && findDestEmplacements.length === 0 && (
                              <div className="en-find-warn">
                                <AlertCircle size={13}/> Cet entrepôt n'a aucun emplacement actif. Créez d'abord son plan (rayons &amp; emplacements).
                              </div>
                            )}

                            <div className="en-find-hint">
                              Seuls les entrepôts détenant déjà ce produit sont proposés — le rangement déplace du stock à
                              l'intérieur d'un entrepôt. Pour l'envoyer vers un autre entrepôt, passez par les transferts de stock.
                            </div>

                            <div className="en-move-form-row">
                              <button type="button" className="en-btn-confirm" onClick={submitFind} disabled={findBusy}>
                                {findBusy ? <Loader size={13} className="en-spin"/> : <><ArrowRight size={13}/> Ranger</>}
                              </button>
                              {findSource && findSourceMax > 0 && (
                                <button type="button" className="en-btn-cancel" onClick={() => setFindQty(String(findSourceMax))}>
                                  Tout ({findSourceMax})
                                </button>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </>
                  )}

                  {!findProduit && !findSearch.trim() && (
                    <div className="en-plan-empty">
                      <PackageSearch size={28}/>
                      <span>Tapez le nom, le code ou le code-barres d'un produit pour commencer.</span>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="en-modal-footer">
              <button type="button" className="en-btn-cancel" onClick={closeFind}>Fermer</button>
            </div>
          </div>
        </>
      )}

      {/* ── Modal Import stock MULTI-ENTREPÔTS ── */}
      {showMultiImport && (
        <>
          <div className="en-overlay" onClick={closeMultiImport}/>
          <div className="en-modal en-modal--xl">
            <div className="en-modal-head">
              <div>
                <h2 className="en-modal-title"><FileSpreadsheet size={15}/> Import stock multi-entrepôts</h2>
                <p className="en-modal-sub">Un seul fichier pour répartir le stock sur plusieurs entrepôts. Chaque ligne précise l'entrepôt cible.</p>
              </div>
              <button className="en-modal-close" onClick={closeMultiImport} disabled={multiImporting}><X size={16}/></button>
            </div>

            <div className="en-modal-body">
              {!multiResult && (
                <>
                  <div className="en-import-help">
                    <span>Colonnes attendues : <code>Entrepôt</code> (nom ou _id), <code>Code Produit</code> (ou <code>Code Barres</code>), <code>Quantité Stock</code>.</span>
                    <button type="button" className="en-template-btn" onClick={downloadMultiTemplate}>
                      <Download size={12}/> Télécharger le modèle
                    </button>
                  </div>

                  {/* Liste des noms d'entrepôts valides — référence rapide */}
                  {entrepots.length > 0 && (
                    <div className="en-multi-valid">
                      <div className="en-multi-valid-label">
                        Noms d'entrepôts à utiliser dans la colonne <code>Entrepôt</code>&nbsp;:
                      </div>
                      <div className="en-multi-valid-chips">
                        {entrepots.map(e => (
                          <button key={e._id} type="button"
                            className="en-multi-valid-chip"
                            title="Cliquer pour copier"
                            onClick={() => {
                              navigator.clipboard?.writeText(e.nom).then(
                                () => showToast('success', `Nom copié : ${e.nom}`),
                                () => {}
                              );
                            }}>
                            {e.nom}
                          </button>
                        ))}
                      </div>
                      <div className="en-multi-valid-hint">
                        La comparaison est insensible aux accents, à la casse et aux espaces multiples (« Entrepôt » et « Entrepot » fonctionnent tous les deux).
                      </div>
                    </div>
                  )}

                  <div className="en-dropzone" onClick={() => multiFileRef.current?.click()}>
                    <Upload size={20}/>
                    <span>Cliquer pour sélectionner un fichier Excel</span>
                    <small>.xlsx, .xls, .csv — toutes vos boutiques en un seul fichier</small>
                    <input ref={multiFileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={handleMultiFile}/>
                  </div>

                  {multiError && (
                    <div className="en-import-error"><AlertCircle size={12}/> {multiError}</div>
                  )}

                  {multiRows.length > 0 && (
                    <>
                      <div className="en-import-mode">
                        <label className={`en-mode-opt${multiMode === 'remplacement' ? ' en-mode-opt--active' : ''}`}>
                          <input type="radio" name="multiMode" value="remplacement"
                            checked={multiMode === 'remplacement'}
                            onChange={() => setMultiMode('remplacement')}/>
                          <div><strong>Remplacer</strong><span>La quantité écrase la valeur existante</span></div>
                        </label>
                        <label className={`en-mode-opt${multiMode === 'ajout' ? ' en-mode-opt--active' : ''}`}>
                          <input type="radio" name="multiMode" value="ajout"
                            checked={multiMode === 'ajout'}
                            onChange={() => setMultiMode('ajout')}/>
                          <div><strong>Ajouter</strong><span>La quantité s'ajoute au stock existant</span></div>
                        </label>
                      </div>

                      <div className="en-import-preview">
                        <div className="en-preview-head">Aperçu — {multiRows.length} ligne{multiRows.length > 1 ? 's' : ''}, {multiGrouped.length} entrepôt{multiGrouped.length > 1 ? 's' : ''}</div>
                        <div className="en-multi-grouped">
                          {multiGrouped.map(([entrepotName, rows], k) => {
                            const norm = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
                            const targetNorm = norm(entrepotName);
                            const known = entrepots.find(e => norm(e.nom) === targetNorm || e._id === entrepotName);
                            return (
                              <div key={k} className={`en-multi-group ${!known ? 'en-multi-group--unknown' : ''}`}>
                                <div className="en-multi-group-head">
                                  <strong>{entrepotName}</strong>
                                  <span>{rows.length} ligne{rows.length > 1 ? 's' : ''}</span>
                                  {!known && <span className="en-multi-warn">⚠ entrepôt introuvable</span>}
                                </div>
                                <div className="en-multi-group-body">
                                  {rows.slice(0, 8).map((r, i) => (
                                    <div key={i} className="en-multi-line">
                                      <span>{r.codeProduit || r.codeBarres}</span>
                                      <span className="en-multi-line-name">{r.nom || '—'}</span>
                                      <strong>{r.quantiteStock}</strong>
                                    </div>
                                  ))}
                                  {rows.length > 8 && <div className="en-multi-more">+ {rows.length - 8} ligne(s)…</div>}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}

              {multiResult && (
                <div className="en-import-result">
                  <div className="en-import-stats">
                    <div className="en-stat en-stat--green"><strong>{multiResult.totalCrees}</strong><span>Créé(s)</span></div>
                    <div className="en-stat en-stat--blue"><strong>{multiResult.totalMisAJour}</strong><span>Mis à jour</span></div>
                    <div className="en-stat en-stat--red"><strong>{multiResult.erreurs?.length || 0}</strong><span>Erreur(s)</span></div>
                  </div>
                  {multiResult.details?.length > 0 && (
                    <div className="en-multi-detail">
                      <div className="en-preview-head">Répartition par entrepôt</div>
                      <table className="en-journal-table" style={{ marginTop: 6 }}>
                        <thead><tr><th>Entrepôt</th><th>Lignes</th><th>Créés</th><th>Mis à jour</th></tr></thead>
                        <tbody>
                          {multiResult.details.map((d, i) => (
                            <tr key={i}>
                              <td><strong>{d.entrepot}</strong></td>
                              <td>{d.lignes}</td>
                              <td><span style={{ color: '#16a34a', fontWeight: 700 }}>{d.crees}</span></td>
                              <td><span style={{ color: '#2563eb', fontWeight: 700 }}>{d.misAJour}</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {multiResult.erreurs?.length > 0 && (
                    <div className="en-import-errors">
                      <div className="en-preview-head">Erreurs</div>
                      <ul>
                        {multiResult.erreurs.slice(0, 30).map((er, i) => (
                          <li key={i}>Ligne {er.ligne} {er.codeProduit ? `(${er.codeProduit})` : ''} : {er.erreur}</li>
                        ))}
                        {multiResult.erreurs.length > 30 && <li>+ {multiResult.erreurs.length - 30} autre(s)…</li>}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="en-modal-footer">
              {!multiResult ? (
                <>
                  <button type="button" className="en-btn-cancel" onClick={closeMultiImport} disabled={multiImporting}>Annuler</button>
                  <button type="button" className="en-btn-confirm" disabled={multiImporting || !multiRows.length} onClick={handleMultiSubmit}>
                    {multiImporting
                      ? <><Loader size={13} className="en-spin"/> Import en cours…</>
                      : <><Upload size={13}/> Importer {multiRows.length > 0 ? `(${multiRows.length})` : ''}</>}
                  </button>
                </>
              ) : (
                <button type="button" className="en-btn-confirm" onClick={closeMultiImport}>
                  <Check size={13}/> Fermer
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
